import { createClient } from "matrix-js-sdk";
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

const MatrixContext = createContext(null);

export const MatrixProvider = ({ children }) => {
	const [client, setClient] = useState(null);
	const [isReady, setIsReady] = useState(false);

	// Initialize the Matrix Client
	const initMatrix = useCallback(async (config) => {
		if (!config || !config.access_token) return;

		console.log("🔌 Connecting to Matrix...");

		// --- 🔴 FORCE FIX FOR URL 🔴 ---
		// The backend sends "dash.raah.work:3000", but Mobile/Chrome need "dash.raah.work" (Port 443)
		let serverUrl = config.home_server;

		// 1. Ensure HTTPS
		if (serverUrl && !serverUrl.startsWith("http")) {
			serverUrl = "https://" + serverUrl;
		}

		// 2. STRIP PORT 3000 (This fixes the Mobile/Chrome SSL error)
		serverUrl = serverUrl.replace(":3000", "");

		console.log("🔗 Matrix URL Corrected to:", serverUrl);
		// ----------------------------------

		const newClient = createClient({
			baseUrl: serverUrl, // Use the corrected URL
			accessToken: config.access_token,
			userId: config.user_id,
			deviceId: config.device_id,
			timelineSupport: true,
		});

		// ✅ ADDED: Error Listener to catch Invalid Tokens (401)
		// This prevents the infinite retry loop when a token expires
		newClient.on("Session.logged_out", () => {
			console.warn("⚠️ Matrix Session Expired or Invalid Token");
			newClient.stopClient();
			newClient.clearStores();
			setClient(null);
			setIsReady(false);

			// Clear bad data
			localStorage.removeItem("matrix_config");

			// Optional: Redirect to login if you want to force re-auth
			// window.location.href = '/login';
		});

		try {
			// 3. Start the client
			await newClient.startClient({ initialSyncLimit: 10 });
		} catch (error) {
			console.error("Matrix Start Error:", error);
		}

		// 4. Wait for sync
		newClient.once("sync", (state, prevState, res) => {
			if (state === "PREPARED") {
				console.log("✅ Matrix Client Ready!");
				setIsReady(true);
			} else if (state === "ERROR") {
				// If it errors immediately on sync, it might be the token
				console.error("Matrix Sync Error - Checking Token...");
			}
		});

		setClient(newClient);
	}, []);

	const logoutMatrix = useCallback(() => {
		if (client) {
			try {
				client.stopClient();
				client.clearStores();
			} catch (e) {
				console.warn("Error stopping matrix client", e);
			}
		}
		setClient(null);
		setIsReady(false);
		localStorage.removeItem("matrix_config");
	}, [client]);

	// Auto-connect on load
	useEffect(() => {
		if (client) return;

		const savedConfig = localStorage.getItem("matrix_config");
		if (savedConfig) {
			try {
				const config = JSON.parse(savedConfig);
				if (config && config.access_token) {
					initMatrix(config);
				}
			} catch (err) {
				console.error("Failed to parse saved matrix config", err);
				localStorage.removeItem("matrix_config");
			}
		}
	}, [initMatrix, client]);

	return (
		<MatrixContext.Provider
			value={{ client, isReady, initMatrix, logoutMatrix }}
		>
			{children}
		</MatrixContext.Provider>
	);
};

export const useMatrix = () => useContext(MatrixContext);
